import React, { useState, useEffect, useCallback } from 'react';
import { hatsAPI } from '../services/api';
import { safeNumber, formatCurrency, calculateTieredFee } from '../utils/calculations';
import ImageLightbox from './ImageLightbox';

const BrowsePage = ({ onTradeRequest }) => {
  const [hats, setHats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedHat, setSelectedHat] = useState(null);
  const [showHatModal, setShowHatModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [ownerFilter, setOwnerFilter] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  
  // Image lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxItemName, setLightboxItemName] = useState('');

  useEffect(() => {
    // Check URL for owner filter
    const params = new URLSearchParams(window.location.search);
    const owner = params.get('owner');
    setOwnerFilter(owner);
  }, []);

  const loadHats = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch hats
      const res = await hatsAPI.getAll({ sort: sortBy, order: sortOrder, page: currentPage, perPage: itemsPerPage, owner: ownerFilter || undefined });
      
      const hatsData = res?.items || [];
      const totalCount = res?.total || 0;
      console.log('Hats loaded from API:', hatsData); // Debug log
      console.log('Total items from API:', totalCount); // Debug log
      
      if (hatsData.length === 0) {
        // Fallback: if no public hats, try showing the current user's hats
        const token = localStorage.getItem('authToken');
        if (token) {
          try {
            const myHats = await hatsAPI.getMyHats();
            if (Array.isArray(myHats) && myHats.length > 0) {
              console.log('Fallback to my hats:', myHats);
              setHats(myHats);
              setTotalItems(myHats.length);
              return;
            }
          } catch (e) {
            console.warn('Failed to load my hats fallback:', e);
          }
        }
      }
      
      setHats(hatsData);
      setTotalItems(totalCount);
    } catch (error) {
      console.error('Failed to load data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, currentPage, ownerFilter, itemsPerPage]);

  useEffect(() => {
    loadHats();
  }, [loadHats]);

  const sortHats = (hatsToSort) => {
    return [...hatsToSort].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle string values
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const handleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  const handleViewHat = (hat) => {
    setSelectedHat(hat);
    setCurrentImageIndex(0); // Reset to first image
    setShowHatModal(true);
  };

  const closeHatModal = () => {
    setShowHatModal(false);
    setSelectedHat(null);
    setCurrentImageIndex(0);
  };

  const handleTradeRequest = (hat) => {
    if (onTradeRequest) {
      onTradeRequest(hat);
    }
    closeHatModal();
  };

  // Image lightbox functions
  const openImageLightbox = (item, imageIndex = 0) => {
    const images = [];
    if (item.image_url) {
      images.push(item.image_url);
    }
    if (item.additional_images && item.additional_images.length > 0) {
      images.push(...item.additional_images);
    }
    
    if (images.length > 0) {
      setLightboxImages(images);
      setLightboxIndex(imageIndex);
      setLightboxItemName(item.name);
      setLightboxOpen(true);
    }
  };

  const sortedHats = sortHats(hats);
  
  // Calculate pagination using backend total
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentHats = sortedHats; // Backend already returns paginated results

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="browse-page">
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading purses...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="browse-page">
        <div className="container">
          <div className="error-message">
            <p>{error}</p>
            <button onClick={loadHats} className="btn btn-primary">Try Again</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="browse-page">
      <div className="container">
        <div className="browse-header">
          <h1>Browse Purses</h1>
          <p>Discover amazing purses available for trade</p>
        </div>

        <div className="sort-controls">
          <div className="sort-options">
            <label>Sort by:</label>
            <select 
              value={sortBy} 
              onChange={(e) => handleSort(e.target.value)}
              className="sort-select"
            >
              <option value="created_at">Date Added</option>
              <option value="name">Name</option>
              <option value="brand">Brand</option>
              <option value="size">Size</option>
              <option value="condition">Condition</option>
              <option value="value">Value Amount</option>
            </select>
            <button 
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="sort-order-btn"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          <div className="hats-count">
            {sortedHats.length} purse{sortedHats.length !== 1 ? 's' : ''} found
          </div>
        </div>

        <div className="hats-grid">
          {sortedHats.length === 0 ? (
            <div className="no-hats">
              <p>No purses available for browsing at the moment.</p>
            </div>
          ) : (
            <>
              {currentHats.map((hat) => {
                console.log('Rendering hat:', hat.name, 'with ID:', hat._id);
                return (
                  <div key={hat._id} className="compact-hat-card">
                    <div className="hat-image">
                        <img 
                        src={hat.image_url} 
                        alt={hat.name}
                        loading="lazy"
                        onClick={() => openImageLightbox(hat)}
                        style={{ cursor: 'pointer' }}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Available';
                        }}
                      />
                    </div>
                    <div className="compact-hat-info">
                      <h3>{hat.name}</h3>
                      <p className="compact-hat-description">{hat.description}</p>
                      <div className="compact-hat-details">
                        <span className="hat-brand">Brand: {hat.brand}{typeof hat.value === 'number' ? ` (${formatCurrency(safeNumber(hat.value))})` : ''}</span>
                        <span className="hat-size">Size: {hat.size}</span>
                        <span className="hat-condition">Condition: {hat.condition}</span>
                      </div>
                      {hat.owner_closet_name && (
                        <div className="hat-closet" style={{color:'#666', fontSize:'0.9rem', marginTop:4}}>
                          {hat.owner_closet_name}
                        </div>
                      )}
                      <div className="borrow-pill" style={{marginTop:6, color:'#0a0', fontWeight:600}}>{formatCurrency(calculateTieredFee(hat.value))} Borrow</div>
                      <div className="compact-hat-actions">
                        <button 
                          className="btn btn-primary"
                          onClick={() => handleViewHat(hat)}
                          style={{ 
                            backgroundColor: '#d4af37', 
                            color: 'white', 
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: '600'
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination">
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ← Previous
                    </button>
                    
                    <div className="page-numbers">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          className={`pagination-btn page-number ${currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next →
                    </button>
                  </div>
                  
                  <div className="pagination-info">
                    Showing {startIndex + 1}-{endIndex} of {totalItems} purses
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Hat Details Modal */}
      {showHatModal && selectedHat && (
        <div className="modal-overlay">
          <div className="item-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="item-details-content">
              <span className="close" onClick={closeHatModal}>&times;</span>
              <div className="item-image">
                <img 
                  src={(() => {
                    // Prepare all images (main + additional)
                    const allImages = [];
                    if (selectedHat.image_url) {
                      allImages.push(selectedHat.image_url);
                    }
                    if (selectedHat.additional_images && selectedHat.additional_images.length > 0) {
                      allImages.push(...selectedHat.additional_images);
                    }
                    return allImages.length > 0 ? allImages[currentImageIndex] : '';
                  })()}
                  alt={selectedHat.name}
                  onClick={() => openImageLightbox(selectedHat)}
                  style={{ cursor: 'pointer' }}
                />
                {/* Image thumbnails */}
                {(() => {
                  const allImages = [];
                  if (selectedHat.image_url) {
                    allImages.push(selectedHat.image_url);
                  }
                  if (selectedHat.additional_images && selectedHat.additional_images.length > 0) {
                    allImages.push(...selectedHat.additional_images);
                  }
                  return allImages.length > 1 ? (
                    <div className="image-thumbnails">
                      {allImages.map((image, index) => (
                        <div 
                          key={index}
                          className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                          onClick={() => setCurrentImageIndex(index)}
                        >
                          <img 
                            src={image} 
                            alt={`${selectedHat.name} ${index + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
              <div className="item-info">
                <h4>{selectedHat.name}</h4>
                <p>{selectedHat.description}</p>
                <div className="item-details">
                  <span>Brand: {selectedHat.brand}</span>
                  <span>Size: {selectedHat.size}</span>
                  <span>Condition: {selectedHat.condition}</span>
                  {selectedHat.value && <span>Value: ${selectedHat.value}</span>}
                </div>
                <div className="item-actions">
                  <button className="btn btn-primary" onClick={() => handleTradeRequest(selectedHat)}>Request Trade</button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      window.location.href = `/closet/${selectedHat.user_id}`;
                    }}
                  >
                    View Owner's Closet
                  </button>
                  <button className="btn btn-secondary" onClick={closeHatModal}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        itemName={lightboxItemName}
        onClose={() => setLightboxOpen(false)}
        onIndexChange={setLightboxIndex}
      />
    </div>
  );
};

export default BrowsePage;

// Add CSS styles for image thumbnails in BrowsePage modal
const styles = `
  .item-image {
    position: relative;
  }
  
  .image-thumbnails {
    display: flex;
    gap: 8px;
    margin-top: 10px;
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .thumbnail {
    width: 50px;
    height: 50px;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  
  .thumbnail:hover {
    border-color: #007bff;
    transform: scale(1.05);
  }
  
  .thumbnail.active {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
  }
  
  .thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
} 